/**
 * Client-side helpers mirroring backend/surveys/validate.py.
 */

import { isValidEmail } from "../../lib/accountCredentials";
import { isValidE164 } from "../ui/phoneUtils";

export const OTHER_OPTION_ID = "other";
export const PUBLIC_FEEDBACK_SURVEY_ID = "public-feedback";
export const SOLO_LEADER_SURVEY_ID = "solo-leader-verification";

/**
 * @param {object|null|undefined} condition
 * @param {Record<string, unknown>} answers
 * @returns {boolean}
 */
export function evaluateVisibleIf(condition, answers) {
  if (!condition) return true;
  const actual = answers[condition.questionId];
  const expected = condition.value;
  if (condition.op === "eq") return choiceSelectedId(actual) === expected;
  if (condition.op === "neq") return choiceSelectedId(actual) !== expected;
  if (condition.op === "includes") {
    return choiceSelectedIds(actual).includes(expected);
  }
  if (condition.op === "in") {
    return Array.isArray(expected) && expected.includes(choiceSelectedId(actual));
  }
  return true;
}

/**
 * @param {unknown} value
 * @returns {unknown}
 */
function choiceSelectedId(value) {
  if (value && typeof value === "object" && !Array.isArray(value) && "other" in value && !("ids" in value)) {
    return OTHER_OPTION_ID;
  }
  return value;
}

/**
 * @param {unknown} value
 * @returns {unknown[]}
 */
function choiceSelectedIds(value) {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object" && Array.isArray(value.ids)) return value.ids;
  return [];
}

/**
 * @param {unknown} value
 * @returns {{ optionId: string, otherText: string }}
 */
export function parseSingleChoice(value) {
  if (value && typeof value === "object" && !Array.isArray(value) && typeof value.other === "string") {
    return { optionId: OTHER_OPTION_ID, otherText: value.other };
  }
  if (typeof value === "string") return { optionId: value, otherText: "" };
  return { optionId: "", otherText: "" };
}

/**
 * @param {string} optionId
 * @param {string} [otherText]
 * @returns {unknown}
 */
export function serializeSingleChoice(optionId, otherText = "") {
  if (optionId === OTHER_OPTION_ID) {
    return { other: otherText };
  }
  return optionId;
}

/**
 * @param {unknown} value
 * @returns {{ ids: string[], otherText: string }}
 */
export function parseMultiChoice(value) {
  if (Array.isArray(value)) {
    return { ids: value.filter((id) => typeof id === "string"), otherText: "" };
  }
  if (value && typeof value === "object" && Array.isArray(value.ids)) {
    return {
      ids: value.ids.filter((id) => typeof id === "string"),
      otherText: typeof value.otherText === "string" ? value.otherText : "",
    };
  }
  return { ids: [], otherText: "" };
}

/**
 * @param {string[]} ids
 * @param {string} [otherText]
 * @returns {unknown}
 */
export function serializeMultiChoice(ids, otherText = "") {
  if (ids.includes(OTHER_OPTION_ID)) {
    return { ids, otherText };
  }
  return ids.filter((id) => id !== OTHER_OPTION_ID);
}

/**
 * @param {object} question
 * @param {Record<string, unknown>} answers
 * @returns {boolean}
 */
export function isQuestionVisible(question, answers) {
  return evaluateVisibleIf(question?.visibleIf, answers);
}

/**
 * @param {object} question
 * @param {unknown} value
 * @returns {boolean}
 */
export function isAnswered(question, value) {
  const type = question?.type;
  if (type === "checkbox") {
    if (question?.required) return value === true;
    return true;
  }
  if (value == null) return false;
  if (type === "short_text" || type === "long_text") {
    return typeof value === "string" && value.trim().length > 0;
  }
  if (type === "boolean" || type === "true_false") return typeof value === "boolean";
  if (type === "single_choice") {
    const { optionId, otherText } = parseSingleChoice(value);
    if (!optionId) return false;
    if (optionId === OTHER_OPTION_ID) return otherText.trim().length > 0;
    return true;
  }
  if (type === "multi_choice") {
    const { ids, otherText } = parseMultiChoice(value);
    if (ids.length === 0) return false;
    if (ids.includes(OTHER_OPTION_ID)) return otherText.trim().length > 0;
    return true;
  }
  if (type === "rating" || type === "agree_disagree") {
    return typeof value === "number" && !Number.isNaN(value);
  }
  if (type === "image") {
    if (!Array.isArray(value) || value.length === 0) return false;
    return value.every(
      (item) =>
        item &&
        typeof item.url === "string" &&
        item.url.trim() &&
        typeof item.key === "string" &&
        item.key.trim()
    );
  }
  return false;
}

/**
 * @param {object} definition
 * @returns {object[]}
 */
export function iterQuestions(definition) {
  const pages = definition?.pages || [];
  return pages.flatMap((page) => page.questions || []);
}

/**
 * @param {object} page
 * @param {Record<string, unknown>} answers
 * @returns {object[]}
 */
export function visibleQuestionsOnPage(page, answers) {
  return (page?.questions || []).filter((q) => isQuestionVisible(q, answers || {}));
}

/**
 * @param {object} page
 * @param {Record<string, unknown>} answers
 * @returns {boolean}
 */
export function pageRequiredComplete(page, answers) {
  return visibleQuestionsOnPage(page, answers).every((q) => {
    if (!q.required) return true;
    return isAnswered(q, (answers || {})[q.id]);
  });
}

/**
 * @param {object} definition
 * @param {Record<string, unknown>} answers
 * @returns {boolean}
 */
export function surveyRequiredComplete(definition, answers) {
  return iterQuestions(definition).every((q) => {
    if (!isQuestionVisible(q, answers || {})) return true;
    if (!q.required) return true;
    return isAnswered(q, (answers || {})[q.id]);
  });
}

/**
 * True when Other is selected but the specify field is empty.
 *
 * @param {object} question
 * @param {unknown} value
 * @returns {boolean}
 */
export function isOtherIncomplete(question, value) {
  if (!question?.allowOther) return false;
  if (question.type === "single_choice") {
    const { optionId, otherText } = parseSingleChoice(value);
    return optionId === OTHER_OPTION_ID && otherText.trim().length === 0;
  }
  if (question.type === "multi_choice") {
    const { ids, otherText } = parseMultiChoice(value);
    return ids.includes(OTHER_OPTION_ID) && otherText.trim().length === 0;
  }
  return false;
}

/**
 * @param {object} definition
 * @param {Record<string, unknown>} answers
 * @param {number} [currentPageIndex]
 * @returns {{ answered: number, total: number }}
 */
export function progressCounts(definition, answers, currentPageIndex = 0) {
  const pages = definition?.pages || [];
  const answersMap = answers || {};
  let answered = 0;
  let total = 0;
  pages.forEach((page, pageIndex) => {
    visibleQuestionsOnPage(page, answersMap).forEach((question) => {
      total += 1;
      const value = answersMap[question.id];
      if (isAnswered(question, value)) {
        answered += 1;
        return;
      }
      const skippedOptional =
        pageIndex < currentPageIndex && !question.required && !isOtherIncomplete(question, value);
      if (skippedOptional) answered += 1;
    });
  });
  return { answered, total };
}

/**
 * Format error for email/phone short_text values. Empty values are not format errors.
 *
 * @param {object} question
 * @param {unknown} value
 * @returns {string}
 */
export function questionFormatError(question, value) {
  if (question?.type !== "short_text" || typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (question.inputType === "email" && !isValidEmail(trimmed)) {
    return "Please enter a valid email address";
  }
  if (question.inputType === "phone" && !isValidE164(trimmed)) {
    return "Please enter a valid phone number";
  }
  return "";
}

/**
 * True if this question blocks Next / Submit.
 *
 * @param {object} question
 * @param {unknown} value
 * @returns {boolean}
 */
export function isQuestionBlocking(question, value) {
  if (isOtherIncomplete(question, value)) return true;
  if (questionFormatError(question, value)) return true;
  if (question?.required && !isAnswered(question, value)) return true;
  return false;
}

/**
 * Group refN.* questions into reference blocks for custom layouts.
 *
 * @param {object[]} questions
 * @returns {Array<
 *   | { kind: "question", question: object }
 *   | { kind: "reference", prefix: string, title: string, questions: object[] }
 * >}
 */
export function groupQuestionsForLayout(questions) {
  const items = [];
  const consumed = new Set();
  (questions || []).forEach((question) => {
    if (consumed.has(question.id)) return;
    const match = typeof question.id === "string" ? question.id.match(/^(ref\d+)\./) : null;
    if (!match) {
      items.push({ kind: "question", question });
      return;
    }
    const prefix = match[1];
    const grouped = (questions || []).filter((item) => item.id.startsWith(`${prefix}.`));
    grouped.forEach((item) => consumed.add(item.id));
    const number = prefix.replace(/^ref/i, "");
    items.push({
      kind: "reference",
      prefix,
      title: `Reference ${number}`,
      questions: grouped,
    });
  });
  return items;
}

/**
 * @param {object} page
 * @param {Record<string, unknown>} answers
 * @returns {object|null}
 */
export function firstBlockingOnPage(page, answers) {
  return (
    visibleQuestionsOnPage(page, answers).find((q) =>
      isQuestionBlocking(q, (answers || {})[q.id])
    ) || null
  );
}

/**
 * @param {object} definition
 * @param {Record<string, unknown>} answers
 * @returns {object|null}
 */
export function firstBlockingInSurvey(definition, answers) {
  return (
    iterQuestions(definition).find((q) => {
      if (!isQuestionVisible(q, answers || {})) return false;
      return isQuestionBlocking(q, (answers || {})[q.id]);
    }) || null
  );
}

/**
 * @param {object} definition
 * @param {string} questionId
 * @returns {number}
 */
export function pageIndexForQuestionId(definition, questionId) {
  const pages = definition?.pages || [];
  return pages.findIndex((page) => (page.questions || []).some((q) => q.id === questionId));
}

/**
 * @param {object} definition
 * @param {string} [pageId]
 * @returns {number}
 */
export function pageIndexForId(definition, pageId) {
  const pages = definition?.pages || [];
  const index = pages.findIndex((page) => page.id === pageId);
  return index >= 0 ? index : 0;
}

/**
 * @param {string} iso
 * @returns {string}
 */
export function formatSubmittedAt(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}
